/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  DICOM_TAG_FORMAT,
  DYNAMIC_DURATION_PATTERN,
  DYNAMIC_PERIOD_PATTERN,
  DYNAMIC_FORMAT_DATE_AND_TIME,
  HL7_FIELD_FORMAT,
  BETWEEN,
  NOT_BETWEEN,
  DAY_OF_WEEK,
  DICOM,
  EQUAL,
  GREATER,
  GREATER_OR_EQUAL,
  IN_RANGE,
  LESS,
  LESS_OR_EQUAL,
  LIKE,
  NOT_EQUAL,
  NOT_LIKE,
  TIME_OF_DAY,
  HL7,
  OUT_OF_RANGE,
  SCRIPT,
  DYNAMIC,
  DATE_INTERVAL,
  TIME_INTERVAL,
  DATE_AND_TIME_INTERVAL,
  BETWEEN_DATE_AND_TIME_INTERVAL,
  BETWEEN_DATE_INTERVAL,
  BETWEEN_TIME_INTERVAL,
  ORGANISATION,
  SITE,
  MODALITY,
  REPORTING_PRIORITY,
  BODY_PART,
  GENDER,
  EXAM_CODE,
  AGE_RANGE,
  STUDY_DESCRIPTION,
  CONTAINS,
  NOT_CONTAINS,
  PATIENT_CLASS,
} from "../../constant";
import {
  ConditionError,
  ConditionRuleData,
  UntaggedConditionObjectData,
} from "@advapacs/common";
import { checkValidatePeriod } from "../Transforms/helpers";

class Validator {
  errors: ConditionError[] = [];

  static isValidDateIntervalVal(value: UntaggedConditionObjectData): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isValid = !Validator.validateDynamicPeriod(dynamic?.value);
      return (
        dynamic?.value &&
        dynamic?.operator &&
        dynamic?.operator === DATE_INTERVAL &&
        isValid
      );
    }
    return false;
  }

  static isValidTimeIntervalVal(value: UntaggedConditionObjectData): boolean {
    // It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValid = !Validator.validateDynamicDuration(dynamic?.value); // show invalid if it's false
      return (
        dynamic?.value &&
        dynamic?.operator &&
        dynamic?.operator === TIME_INTERVAL &&
        isValid
      );
    }
    return false;
  }

  static isValidDateAndTimeIntervalVal(
    value: UntaggedConditionObjectData
  ): boolean {
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = dynamic?.value && checkValidatePeriod(dynamic?.value);
      if (isPeriod) {
        return (
          dynamic?.value &&
          dynamic?.operator &&
          dynamic?.operator === DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicPeriod(dynamic?.value)
        );
      } else {
        return (
          dynamic?.value &&
          dynamic?.operator &&
          dynamic?.operator === DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicDuration(dynamic?.value)
        );
      }
    }
    return false;
  }

  static isValidBetweenDateIntervalFrom(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isValidPeriod = !Validator.validateDynamicPeriod(dynamic?.from);
      return (
        dynamic?.from &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_DATE_INTERVAL &&
        isValidPeriod
      );
    }
    return false;
  }

  static isValidBetweenTimeIntervalFrom(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValidFrom = !Validator.validateDynamicDuration(dynamic?.from);
      return (
        dynamic?.from &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_TIME_INTERVAL &&
        isValidFrom
      );
    }
    return false;
  }

  static isValidBetweenDateAndTimeIntervalFrom(
    value: UntaggedConditionObjectData
  ): boolean {
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = dynamic?.from && checkValidatePeriod(dynamic?.from);
      if (isPeriod) {
        return (
          dynamic?.from &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicPeriod(dynamic?.from)
        );
      } else {
        return (
          dynamic?.from &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicDuration(dynamic?.from)
        );
      }
    }
    return false;
  }

  static isValidBetweenDateIntervalTo(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Period P2D,P3Y3M2D
    const { dynamic } = value;
    if (dynamic) {
      const isValidPeriodTo = !Validator.validateDynamicPeriod(dynamic?.to);
      return (
        dynamic?.to &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_DATE_INTERVAL &&
        isValidPeriodTo
      );
    }
    return false;
  }

  static isValidBetweenTimeIntervalTo(
    value: UntaggedConditionObjectData
  ): boolean {
    // It's Duration PT13M,PT2H1M
    const { dynamic } = value;
    if (dynamic) {
      const isValidTo = !Validator.validateDynamicDuration(dynamic?.to);
      return (
        dynamic?.to &&
        dynamic?.operator &&
        dynamic?.operator === BETWEEN_TIME_INTERVAL &&
        isValidTo
      );
    }
    return false;
  }

  static isValidBetweenDateAndTimeIntervalTo(
    value: UntaggedConditionObjectData
  ): boolean {
    // if value of dynamic in ["Y", "M", "W", "D"] and without T => it's check Period
    const { dynamic } = value;
    if (dynamic) {
      const isPeriod = dynamic?.to && checkValidatePeriod(dynamic?.to);
      if (isPeriod) {
        return (
          dynamic?.from &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicPeriod(dynamic?.to)
        );
      } else {
        return (
          dynamic?.from &&
          dynamic?.operator &&
          dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL &&
          !Validator.validateDynamicDuration(dynamic?.to)
        );
      }
    }
    return false;
  }

  static isValidDynamicTag(value: UntaggedConditionObjectData): boolean {
    const { dynamic } = value;
    if (dynamic) {
      return (
        !dynamic?.tag || // invalid tag
        !Validator.validateTag(dynamic?.tag)
      );
    }

    return false;
  }

  static isValidDynamicBetweenOperators(
    value: UntaggedConditionObjectData
  ): boolean {
    const { dynamic } = value;
    if (dynamic) {
      return (
        [
          BETWEEN_DATE_AND_TIME_INTERVAL,
          BETWEEN_DATE_INTERVAL,
          BETWEEN_TIME_INTERVAL,
        ].includes(dynamic?.operator) &&
        !dynamic?.from &&
        !dynamic?.to
      );
    }
    return false;
  }

  static isValidDynamicNoBetweenOperators(
    value: UntaggedConditionObjectData
  ): boolean {
    const { dynamic } = value;
    if (dynamic) {
      return (
        [DATE_INTERVAL, TIME_INTERVAL, DATE_AND_TIME_INTERVAL].includes(
          dynamic?.operator
        ) && !dynamic?.value
      );
    }
    return false;
  }

  static validateCondition(value: UntaggedConditionObjectData): boolean {
    const { type, dynamic } = value;
    if (!type) {
      return false;
    }

    switch (type) {
      case ORGANISATION:
      case SITE:
      case MODALITY:
      case BODY_PART:
      case GENDER:
      case EXAM_CODE:
      case PATIENT_CLASS:
        return (
          value?.stringCollectionProperty?.operator &&
          value?.stringCollectionProperty?.values &&
          value?.stringCollectionProperty?.values?.length > 0
        );

      case REPORTING_PRIORITY:
        return (
          !!value?.stringCollectionProperty?.values ||
          value?.stringCollectionProperty?.values?.length <= 0
        );

      case AGE_RANGE:
        const operator = value?.integerRangeProperty?.operator;
        return (
          !!operator &&
          !(
            ([EQUAL, GREATER, GREATER_OR_EQUAL, LESS, LESS_OR_EQUAL].includes(
              operator
            ) && // Required value in : Equal/Greater/...
              !value?.integerRangeProperty?.value) ||
            ([BETWEEN, NOT_BETWEEN].includes(operator) && // Required from/to
              ((!value?.integerRangeProperty?.from &&
                value?.integerRangeProperty?.from != "0") ||
                (!value?.integerRangeProperty?.to &&
                  value?.integerRangeProperty?.to != "0")))
          )
        );
      case STUDY_DESCRIPTION:
        const stdOperator = value?.stringProperty?.operator;
        return (
          !!stdOperator &&
          !(
            ([
              EQUAL,
              GREATER,
              GREATER_OR_EQUAL,
              LESS,
              LESS_OR_EQUAL,
              LIKE,
              NOT_LIKE,
              NOT_EQUAL,
              CONTAINS,
              NOT_CONTAINS,
            ].includes(stdOperator) && // Required value in : Equal/Greater/...
              !value?.stringProperty?.value) ||
            ([IN_RANGE, OUT_OF_RANGE].includes(stdOperator) && // Required from/to
              ((!value?.stringProperty?.from &&
                value?.stringProperty?.from != "0") ||
                (!value?.stringProperty?.to &&
                  value?.stringProperty?.to != "0")))
          )
        );
      case HL7:
        return !(
          !value?.hl7?.path ||
          !Validator.validateHL7Field(value?.hl7?.path) ||
          !value?.hl7?.operator ||
          ([
            EQUAL,
            GREATER,
            GREATER_OR_EQUAL,
            LESS,
            LESS_OR_EQUAL,
            LIKE,
            NOT_EQUAL,
            NOT_LIKE,
          ].includes(value?.hl7?.operator) &&
            !value?.hl7?.value) ||
          ([IN_RANGE].includes(value?.hl7?.operator) &&
            (!value?.hl7?.from || !value?.hl7?.to))
        );
      case DYNAMIC:
        // Validate Period base on date and duration base on time
        return !(
          Validator.isValidDynamicTag(value) ||
          !dynamic?.operator ||
          Validator.isValidDynamicNoBetweenOperators(value) ||
          Validator.isValidDynamicBetweenOperators(value) ||
          Validator.isValidDateIntervalVal(value) ||
          Validator.isValidTimeIntervalVal(value) ||
          Validator.isValidDateAndTimeIntervalVal(value) ||
          Validator.isValidBetweenDateIntervalFrom(value) ||
          Validator.isValidBetweenTimeIntervalFrom(value) ||
          Validator.isValidBetweenDateAndTimeIntervalFrom(value) ||
          Validator.isValidBetweenDateIntervalTo(value) ||
          Validator.isValidBetweenTimeIntervalTo(value) ||
          Validator.isValidBetweenDateAndTimeIntervalTo(value)
        );
      case DICOM:
        return !(
          !value?.dicom?.tag ||
          !Validator.validateTag(value?.dicom?.tag) ||
          !value?.dicom?.operator ||
          ([
            EQUAL,
            GREATER,
            GREATER_OR_EQUAL,
            LESS,
            LESS_OR_EQUAL,
            LIKE,
            NOT_EQUAL,
            NOT_LIKE,
          ].includes(value?.dicom?.operator) &&
            !value?.dicom?.value) ||
          ([IN_RANGE, OUT_OF_RANGE].includes(value?.dicom?.operator) &&
            (!value?.dicom?.from || !value?.dicom?.to))
        );
      case TIME_OF_DAY:
        return !(
          !value?.timeOfDay?.operator ||
          ([LESS, GREATER, GREATER_OR_EQUAL, LESS_OR_EQUAL].includes(
            value?.timeOfDay?.operator
          ) &&
            !value?.timeOfDay?.value) ||
          ([BETWEEN, NOT_BETWEEN].includes(value?.timeOfDay?.operator) &&
            (!value?.timeOfDay?.from || !value?.timeOfDay?.to))
        );
      case DAY_OF_WEEK:
        return !(
          !value?.dayOfWeek?.operator ||
          ([EQUAL, NOT_EQUAL].includes(value?.dayOfWeek?.operator) &&
            !value?.dayOfWeek?.value) ||
          ([BETWEEN, NOT_BETWEEN].includes(value?.dayOfWeek?.operator) &&
            (!value?.dayOfWeek?.from || !value?.dayOfWeek?.to))
        );
      case SCRIPT:
        return !(
          !value?.script ||
          (value.script?.annotations &&
            value.script.annotations?.length > 0 &&
            value.script.annotations.filter((ann) => ann.type === "error")
              .length > 0)
        );
      default:
        return false;
    }
  }

  static validateHL7Field(field: string): boolean {
    if (!field) {
      return true;
    }

    return HL7_FIELD_FORMAT.test(field.trim());
  }

  static validateOrganisationId(field?: string): boolean {
    return !!field;
  }

  static validateSiteId(field?: string): boolean {
    return !!field;
  }

  static validateModality(field?: string): boolean {
    return !!field;
  }

  // Number of orders validator.
  static validateNumberOfOrdersValue(field?: number): boolean {
    return !(field < 1 || field > 999);
  }

  static validateNumberOfOrdersFrom(from?: number, to?: number): boolean {
    return !(from < 1 || from > 999 || from > to);
  }

  static validateNumberOfOrdersTo(to?: number, from?: number): boolean {
    return !(to < 1 || to > 999 || to < from);
  }

  static validateTag(tag: string): boolean {
    if (tag) {
      const results: boolean[] = [];
      tag
        .trim()
        .split(".")
        .forEach((comp) => {
          if (comp) {
            results.push(DICOM_TAG_FORMAT.test(comp));
          }
        });
      return !results.includes(false);
    }

    return false;
  }

  static validateStudyDescription(field?: string): boolean {
    return !!field;
  }

  static validateDynamicDuration(dynamic: string): boolean {
    if (dynamic) {
      const results: boolean[] = [];
      dynamic
        .trim()
        .split(".")
        .forEach((comp) => {
          if (comp) {
            results.push(DYNAMIC_DURATION_PATTERN.test(comp));
          }
        });
      return !results.includes(false);
    }
    return false;
  }

  static validateDynamicPeriod(dynamic: string): boolean {
    const regex = DYNAMIC_PERIOD_PATTERN;
    if (dynamic) {
      const results: boolean[] = [];
      dynamic
        .trim()
        .split(".")
        .forEach((comp) => {
          if (comp) {
            results.push(regex.test(comp));
          }
        });
      return !results.includes(false);
    }
    return false;
  }

  static validateBothDurationAndPeriod(dynamic: string): boolean {
    const regex = DYNAMIC_FORMAT_DATE_AND_TIME;
    if (dynamic) {
      const results: boolean[] = [];
      dynamic
        .trim()
        .split(".")
        .forEach((comp) => {
          if (comp) {
            results.push(regex.test(comp));
          }
        });
      return !results.includes(false);
    }
    return false;
  }

  validate(rules: ConditionRuleData): ConditionError[] {
    rules.conditionRules.forEach((item) => {
      this.validate(item);
    });

    rules.conditions.forEach((item) => {
      const conditionValid = Validator.validateCondition(item);
      if (!conditionValid) {
        const { type, id } = item;
        this.errors.push({
          id,
          type,
        });
      }
    });

    return this.errors;
  }
}

export default Validator;
